import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  ArrowUpRight, Pencil, Plus, Save, Trash2, Undo2,
} from 'lucide-react';
import {
  Actions, Button, Card, CardTitle, Field, Input, Message, Modal, ModalActions, ModalField, ModalTitle,
  Overlay, Select,
} from '../admin/ui';
import * as Api from './EmailAdminApi';
import ScheduleTimeline from './ScheduleTimeline';
import { TemplateProblems } from './TemplateParts';
import {
  errorText, saveProblems, scheduleName, scheduleSentence, scheduleSteps,
} from './emailAdminModel';

// One automatic email type (P4b): what it does now in one sentence, its on/off and send time, either
// the gap and maximum (invite reminders) or the countdown's days. The settings are edited locally and
// saved explicitly; adding, changing and removing a day are their own actions behind a dialog that
// states the consequence. The backend's `problems` are the truth and are shown verbatim.
//
// The facts the owner wants in words (Vienna time and the 5-minute tick, a changed time applies from
// the next run, missed days are not sent later, changing a day keeps its history, removing a day
// keeps its history but re-adding it is new, a new day starts as a copy, and the invite rules) are
// hints on the card and in the dialogs, never a second validator.
const Hint = styled.p`
  margin: .35rem 0 0;
  font-size: .76rem;
  line-height: 1.45;
  color: #64748b;
`;

const Settings = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: .8rem 1rem;
  align-items: flex-end;
  margin-top: 1rem;
`;

const SwitchRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  font-size: .85rem;
  font-weight: 700;
  color: #334155;
  padding-bottom: .55rem;
`;

const DayRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: .6rem 1rem;
  align-items: flex-start;
  justify-content: space-between;
  padding: .7rem 0;
  border-top: 1px solid #eef2f7;

  &:first-of-type {
    border-top: 0;
  }
`;

const DayText = styled.div`
  flex: 1 1 16rem;
  min-width: 0;
`;

const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: .35rem .9rem;
  margin-top: .25rem;
`;

const TemplateLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: .25rem;
  color: #0369a1;
  font-size: .8rem;
  font-weight: 700;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const SectionTitle = styled.h3`
  font-size: .9rem;
  margin: 1.25rem 0 .25rem;
  color: #111D4E;
`;

const numberOrNull = (value) => (value === '' || value === null || value === undefined ? null : Number(value));

const ScheduleCard = ({ schedule, onReplace, onDirtyChange, focused }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);

  const repeating = schedule.shape === 'REPEATING';
  const [enabled, setEnabled] = useState(schedule.enabled);
  const [sendTime, setSendTime] = useState(schedule.sendTime);
  const [intervalDays, setIntervalDays] = useState(schedule.intervalDays ?? '');
  const [maxCount, setMaxCount] = useState(schedule.maxCount ?? '');
  const [problems, setProblems] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [dialogProblems, setDialogProblems] = useState([]);
  const [dialogError, setDialogError] = useState('');
  const [dialogBusy, setDialogBusy] = useState(false);

  // A save or a day change answers the whole schedule; its settings are the new baseline. The
  // initial state already is that baseline, so the effect compares the saved settings and never
  // clobbers an edit with the mount values. The messages of a save stay, so the admin sees it worked.
  const savedKey = JSON.stringify([
    schedule.enabled, schedule.sendTime, schedule.intervalDays, schedule.maxCount,
  ]);
  const lastSaved = useRef(savedKey);
  useEffect(() => {
    if (lastSaved.current === savedKey) return;
    lastSaved.current = savedKey;
    setEnabled(schedule.enabled);
    setSendTime(schedule.sendTime);
    setIntervalDays(schedule.intervalDays ?? '');
    setMaxCount(schedule.maxCount ?? '');
    setProblems([]);
    setError('');
  }, [savedKey, schedule]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = enabled !== schedule.enabled
    || sendTime !== schedule.sendTime
    || (repeating && (String(intervalDays) !== String(schedule.intervalDays ?? '')
      || String(maxCount) !== String(schedule.maxCount ?? '')));

  // The screen asks before a tab switch or a link leaves; the card only reports.
  useEffect(() => {
    onDirtyChange(schedule.kind, dirty);
  }, [dirty, schedule.kind, onDirtyChange]);
  useEffect(() => () => onDirtyChange(schedule.kind, false), [schedule.kind, onDirtyChange]);

  const steps = useMemo(() => scheduleSteps(schedule), [schedule]);
  const cardId = `schedule-${schedule.kind}`;

  const reset = () => {
    setEnabled(schedule.enabled);
    setSendTime(schedule.sendTime);
    setIntervalDays(schedule.intervalDays ?? '');
    setMaxCount(schedule.maxCount ?? '');
    setProblems([]);
    setError('');
    setMessage('');
  };

  const save = async () => {
    setSaving(true);
    setProblems([]);
    setError('');
    setMessage('');
    const body = repeating
      ? {
        enabled,
        sendTime,
        intervalDays: numberOrNull(intervalDays),
        maxCount: numberOrNull(maxCount),
      }
      : { enabled, sendTime };
    try {
      const next = await Api.updateSchedule(schedule.kind, body);
      onReplace(next);
      setMessage(t('emailAdmin.schedules.saved', 'Saved. The change applies from the next run.'));
    } catch (err) {
      const refused = saveProblems(err);
      if (refused) setProblems(refused);
      else setError(errorText(err, t('emailAdmin.schedules.saveFailed', 'Could not save the schedule.')));
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => {
    setDialogProblems([]);
    setDialogError('');
    setDialog({ type: 'add', day: '', copyFrom: steps[0] ? String(steps[0].id) : '' });
  };
  const openChange = (step) => {
    setDialogProblems([]);
    setDialogError('');
    setDialog({ type: 'change', step, day: String(step.daysBeforeStart) });
  };
  const openRemove = (step) => {
    setDialogProblems([]);
    setDialogError('');
    setDialog({ type: 'remove', step });
  };

  const runDialog = async () => {
    setDialogBusy(true);
    setDialogProblems([]);
    setDialogError('');
    try {
      let next;
      if (dialog.type === 'add') {
        next = await Api.addScheduleStep(schedule.kind, {
          daysBeforeStart: Number(dialog.day),
          copyFromStepId: Number(dialog.copyFrom),
        });
      } else if (dialog.type === 'change') {
        next = await Api.changeScheduleStep(schedule.kind, dialog.step.id, {
          daysBeforeStart: Number(dialog.day),
        });
      } else {
        next = await Api.removeScheduleStep(schedule.kind, dialog.step.id);
      }
      onReplace(next);
      setDialog(null);
    } catch (err) {
      const refused = saveProblems(err);
      if (refused) setDialogProblems(refused);
      else setDialogError(errorText(err, t('emailAdmin.schedules.dayFailed', 'Could not change the day.')));
    } finally {
      setDialogBusy(false);
    }
  };

  const dialogTitle = dialog && {
    add: t('emailAdmin.schedules.dialog.add.title', 'Add a day'),
    change: t('emailAdmin.schedules.dialog.change.title', 'Change the day'),
    remove: t('emailAdmin.schedules.dialog.remove.title', 'Remove this day?'),
  }[dialog.type];

  const dialogConsequence = dialog && {
    add: t('emailAdmin.schedules.dialog.add.consequence',
      'The new day starts with a copy of the text of the day you pick; from then on it has its own text. Adding a day that was removed before is a new day – people the old one reached can get it again.'),
    change: t('emailAdmin.schedules.dialog.change.consequence',
      "Changing a day's number keeps it the same day: people it already reached do not get it again."),
    remove: t('emailAdmin.schedules.dialog.remove.consequence',
      'Removing a day keeps its history; its saved text is deleted. Adding the same day again is a new day, so people the old one reached can get it again.'),
  }[dialog.type];

  return (
    <Card id={cardId} aria-labelledby={`${cardId}-title`}
      style={focused ? { borderLeft: '4px solid #47ABDB' } : undefined}>
      <CardTitle id={`${cardId}-title`} style={{ marginBottom: '.35rem', overflowWrap: 'break-word' }}>
        {scheduleName(t, schedule.kind)}
      </CardTitle>
      <p style={{ margin: 0, color: '#334155', fontSize: '.88rem' }}>
        {scheduleSentence(intl, schedule)}
      </p>

      <Settings>
        <SwitchRow>
          <input type="checkbox" role="switch" checked={enabled}
            onChange={(event) => { setMessage(''); setEnabled(event.target.checked); }} />
          <FormattedMessage id="emailAdmin.schedules.enabled" defaultMessage="Automatic sending" />
        </SwitchRow>
        <Field style={{ flex: '0 1 11rem' }}>
          <FormattedMessage id="emailAdmin.schedules.sendTime" defaultMessage="Send time (Vienna time)" />
          <Input type="time" step="300" value={sendTime}
            onChange={(event) => { setMessage(''); setSendTime(event.target.value); }} />
        </Field>
        {repeating && (
          <>
            <Field style={{ flex: '0 1 9rem' }}>
              <FormattedMessage id="emailAdmin.schedules.intervalDays" defaultMessage="Gap (days)" />
              <Input type="number" min="1" max="30" value={intervalDays}
                onChange={(event) => { setMessage(''); setIntervalDays(event.target.value); }} />
            </Field>
            <Field style={{ flex: '0 1 12rem' }}>
              <FormattedMessage id="emailAdmin.schedules.maxCount" defaultMessage="Maximum automatic reminders" />
              <Input type="number" min="0" max="10" value={maxCount}
                onChange={(event) => { setMessage(''); setMaxCount(event.target.value); }} />
            </Field>
          </>
        )}
      </Settings>

      <Hint>
        <FormattedMessage id="emailAdmin.schedules.sendTimeHint"
          defaultMessage="Vienna time. The email goes out within 5 minutes after this time. A changed time applies from the next run – if today's run has already happened, it applies tomorrow." />
        {' '}
        {schedule.lastRunOn
          ? t('emailAdmin.schedules.lastRun', 'Last automatic run: {date}.', {
            date: intl.formatDate(new Date(`${schedule.lastRunOn}T12:00:00`), { dateStyle: 'medium' }),
          })
          : t('emailAdmin.schedules.neverRun', 'No automatic run yet.')}
      </Hint>
      <Hint>
        {repeating
          ? t('emailAdmin.schedules.inviteHint',
            'Every {days, plural, one {day} other {# days}} after the invitation or the last reminder, at most {max, plural, one {# automatic reminder} other {# automatic reminders}}. They stop when the person answers, when reminders are stopped for them, or when the event has started. Lowering the maximum stops further reminders for people already at or past it. The manual reminder in the program screen works even when this is off.',
            { days: schedule.intervalDays, max: schedule.maxCount })
          : t('emailAdmin.schedules.countdownHint',
            "The days count back from the event's start. A day that was missed (the server was down all day) is not sent later.")}
      </Hint>
      {!enabled && (
        <Hint>
          <FormattedMessage id="emailAdmin.schedules.offHint"
            defaultMessage="Off: no automatic emails of this type at all. The settings stay saved and apply again when you turn it on." />
        </Hint>
      )}

      <TemplateProblems problems={problems} titleId="emailAdmin.schedules.problems"
        titleDefault="This can't be saved yet:" />
      {error && <Message error role="alert">{error}</Message>}
      {message && <Message>{message}</Message>}

      <Actions style={{ marginTop: '.75rem' }}>
        <Button type="button" secondary disabled={!dirty || saving} onClick={reset}>
          <Undo2 size={15} /><FormattedMessage id="emailAdmin.schedules.discard" defaultMessage="Discard" />
        </Button>
        <Button type="button" disabled={!dirty || saving} onClick={save}>
          <Save size={15} />{saving
            ? <FormattedMessage id="emailAdmin.schedules.saving" defaultMessage="Saving…" />
            : <FormattedMessage id="emailAdmin.schedules.save" defaultMessage="Save settings" />}
        </Button>
      </Actions>

      {!repeating && (
        <>
          <SectionTitle>
            <FormattedMessage id="emailAdmin.schedules.days.title" defaultMessage="Days before the event starts" />
          </SectionTitle>
          <ScheduleTimeline schedule={schedule} />
          <div>
            {steps.map((step) => (
              <DayRow key={step.id}>
                <DayText>
                  <div style={{ fontWeight: 700, color: '#111D4E' }}>
                    {t('emailAdmin.schedules.dayRowLabel',
                      '{days, plural, one {# day} other {# days}} before the event starts',
                      { days: step.daysBeforeStart })}
                  </div>
                  <Links>
                    {(step.templates || []).map((template) => (
                      <TemplateLink key={template.key}
                        to={`/admin/emails?tab=emails&key=${template.key}`}
                        title={t('emailAdmin.schedules.openTemplate', 'Open this text in the template editor')}>
                        <ArrowUpRight size={13} />
                        {t(`emailAdmin.schedule.step.audience.${template.audience}`, template.audience)}
                      </TemplateLink>
                    ))}
                  </Links>
                </DayText>
                <Actions style={{ margin: 0 }}>
                  <Button type="button" secondary onClick={() => openChange(step)}>
                    <Pencil size={14} /><FormattedMessage id="emailAdmin.schedules.day.change" defaultMessage="Change day" />
                  </Button>
                  <Button type="button" secondary onClick={() => openRemove(step)}>
                    <Trash2 size={14} /><FormattedMessage id="emailAdmin.schedules.day.remove" defaultMessage="Remove day" />
                  </Button>
                </Actions>
              </DayRow>
            ))}
          </div>
          <Actions style={{ marginTop: '.75rem' }}>
            <Button type="button" secondary onClick={openAdd}>
              <Plus size={15} /><FormattedMessage id="emailAdmin.schedules.day.add" defaultMessage="Add a day" />
            </Button>
          </Actions>
        </>
      )}

      {dialog && (
        <Overlay>
          <Modal role="dialog" aria-modal="true" aria-label={dialogTitle}>
            <ModalTitle>{dialogTitle}</ModalTitle>
            <p style={{ margin: '0 0 1rem', fontSize: '.85rem', color: '#334155', lineHeight: 1.5 }}>
              {dialogConsequence}
            </p>
            {dialog.type === 'add' && (
              <>
                <ModalField>
                  <FormattedMessage id="emailAdmin.schedules.dialog.day" defaultMessage="Days before the event starts" />
                  <Input type="number" min="1" max="60" value={dialog.day}
                    onChange={(event) => setDialog({ ...dialog, day: event.target.value })} />
                </ModalField>
                <ModalField>
                  <FormattedMessage id="emailAdmin.schedules.dialog.copy"
                    defaultMessage="Copy the text of this day" />
                  <Select value={dialog.copyFrom}
                    onChange={(event) => setDialog({ ...dialog, copyFrom: event.target.value })}>
                    {steps.map((step) => (
                      <option key={step.id} value={step.id}>
                        {t('emailAdmin.schedules.dayRowLabel',
                          '{days, plural, one {# day} other {# days}} before the event starts',
                          { days: step.daysBeforeStart })}
                      </option>
                    ))}
                  </Select>
                </ModalField>
              </>
            )}
            {dialog.type === 'change' && (
              <ModalField>
                <FormattedMessage id="emailAdmin.schedules.dialog.day" defaultMessage="Days before the event starts" />
                <Input type="number" min="1" max="60" value={dialog.day}
                  onChange={(event) => setDialog({ ...dialog, day: event.target.value })} />
              </ModalField>
            )}
            <TemplateProblems problems={dialogProblems} titleId="emailAdmin.schedules.problems"
              titleDefault="This can't be saved yet:" />
            {dialogError && <Message error role="alert">{dialogError}</Message>}
            <ModalActions>
              <Button type="button" secondary disabled={dialogBusy} onClick={() => setDialog(null)}>
                <FormattedMessage id="emailAdmin.schedules.dialog.cancel" defaultMessage="Cancel" />
              </Button>
              <Button type="button" danger={dialog.type === 'remove'} disabled={dialogBusy} onClick={runDialog}>
                {dialogBusy
                  ? <FormattedMessage id="emailAdmin.schedules.saving" defaultMessage="Saving…" />
                  : {
                    add: <FormattedMessage id="emailAdmin.schedules.dialog.add.confirm" defaultMessage="Add day" />,
                    change: <FormattedMessage id="emailAdmin.schedules.dialog.change.confirm" defaultMessage="Save day" />,
                    remove: <FormattedMessage id="emailAdmin.schedules.dialog.remove.confirm" defaultMessage="Remove day" />,
                  }[dialog.type]}
              </Button>
            </ModalActions>
          </Modal>
        </Overlay>
      )}
    </Card>
  );
};

export default ScheduleCard;
